import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Form, Formik } from "formik";
import { useToast } from "hooks/useToast";
import {
  useCreateCrmLayoutNodeMutation,
  useUpdateCrmLayoutNodeMutation,
} from "store/apis/crmFields";
import {
  CRM_FIELD_TYPE,
  CRM_FIELD_TYPE_LABEL,
  CrmField,
  CrmFieldType,
  CrmSection,
  OPTION_BACKED_FIELD_TYPES,
} from "types/Models/crmFields";

const FIELD_TYPE_OPTIONS = Object.entries(CRM_FIELD_TYPE_LABEL).map(
  ([value, label]) => ({ value, label })
);

type FieldFormValues = {
  label: string;
  field_type: string;
  help_text: string;
  options: string;
};

const parseOptions = (rawOptions: string) =>
  rawOptions
    .split("\n")
    .map((option) => option.trim())
    .filter(Boolean)
    .map((option) => ({ label: option }));

/**
 * Admin builder for a CRM field. Creating adds the definition and places it on `section`;
 * editing updates the shared definition, so the change lands on every tab showing the field.
 */
export const CRMFieldFormModal = ({
  isOpen,
  close,
  section,
  field,
}: {
  isOpen: boolean;
  close: () => void;
  section?: CrmSection | null;
  field?: CrmField | null;
}) => {
  const { alertToast, successToast } = useToast();
  const [createNode, { isLoading: isCreating }] =
    useCreateCrmLayoutNodeMutation();
  const [updateNode, { isLoading: isUpdating }] =
    useUpdateCrmLayoutNodeMutation();
  const isEdit = Boolean(field);

  const initialValues: FieldFormValues = {
    label: field?.label || "",
    field_type: String(field?.field_type ?? CRM_FIELD_TYPE.TEXT),
    help_text: field?.help_text || "",
    options: (field?.options || []).map((option) => option.label).join("\n"),
  };

  const onSubmit = async (values: FieldFormValues) => {
    const fieldType = Number(values.field_type) as CrmFieldType;
    const options = parseOptions(values.options);

    if (OPTION_BACKED_FIELD_TYPES.includes(fieldType) && options.length === 0) {
      alertToast({ message: "Add at least one option for a select field" });
      return;
    }

    try {
      if (isEdit) {
        await updateNode({
          field: {
            node_id: field!.uuid,
            label: values.label,
            field_type: fieldType,
            help_text: values.help_text || null,
          },
        }).unwrap();
      } else {
        await createNode({
          field: {
            parent_id: section!.uuid,
            label: values.label,
            field_type: fieldType,
            help_text: values.help_text || null,
            options: OPTION_BACKED_FIELD_TYPES.includes(fieldType)
              ? options
              : undefined,
          },
        }).unwrap();
      }
      successToast({ message: isEdit ? "Field updated" : "Field added" });
      close();
    } catch (error: any) {
      alertToast({
        message: error?.data?.message || "Could not save the field",
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
      <Modal.Content useCustomOverlay>
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ values, submitForm }) => {
            const isOptionBacked = OPTION_BACKED_FIELD_TYPES.includes(
              Number(values.field_type) as CrmFieldType
            );

            return (
              <>
                <Modal.Header>
                  <Modal.Title>
                    {isEdit ? "Edit field" : "Add field"}
                  </Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body>
                  <Form className="t-flex t-flex-col t-gap-4 t-m-0">
                    <TextInput name="label" label="Label" required />
                    <Combobox
                      withForm
                      name="field_type"
                      label="Field type"
                      menuPortalTarget={document.body}
                      creatable={false}
                      isClearable={false}
                      options={FIELD_TYPE_OPTIONS}
                      value={FIELD_TYPE_OPTIONS.find(
                        (option) => option.value === values.field_type
                      )}
                    />
                    <TextInput name="help_text" label="Tooltip (optional)" />
                    {isOptionBacked && (
                      <TextArea
                        name="options"
                        label="Options (one per line)"
                        disabled={isEdit}
                        note={
                          isEdit
                            ? "Options of an existing field are managed from the field itself"
                            : undefined
                        }
                      />
                    )}
                  </Form>
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button type="button">Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    onClick={submitForm}
                    isLoading={isCreating || isUpdating}
                    disabled={isCreating || isUpdating || !values.label}
                  >
                    {isEdit ? "Save" : "Add"}
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
